import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Typography, Box, Card, List, ListItemButton, ListItemText, Breadcrumbs, Link, Chip, Button } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { axiosInstance } from "../component/Api/instance";
import { showWarning } from "../utils/popup";
import { UserContext } from "../Context";
import { PrimaryButton } from "../component/ui/Buttons";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionIcon from "@mui/icons-material/Description";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HistoryIcon from "@mui/icons-material/History";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CrownIcon from "../asserts/crown.png";
import PreviousAttemptsModal from "../ModelMockTest/PreviousAttemptsModal";
import TestLeaderboardModal from "../component/Leaderboard/TestLeaderboardModal";

const InstructionPageContainer = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  padding: "32px 0",
  backgroundColor: theme.palette.background.default,
}));

const SidebarCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  padding: "24px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
}));

const InstructionDetailsCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  padding: "40px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  minHeight: "450px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    padding: "24px",
  },
}));

const PaperItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'selected'
})(({ theme, selected }) => ({
  borderRadius: "14px",
  marginBottom: "8px",
  padding: "12px 16px",
  backgroundColor: selected ? `${theme.palette.primary.main}0D` : "transparent",
  border: `1px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
  color: selected ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: selected ? 700 : 500,
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor: selected ? `${theme.palette.primary.main}1A` : theme.palette.action.hover,
  },
}));

export default function NonInstruction() {
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [data, setData] = useState([]);
  const [paperid, setPaperid] = useState(null);
  const [paperTitle, setPaperTitle] = useState("");
  const { sno } = useParams();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [category, setCategory] = useState([]);
  const [attemptsCount, setAttemptsCount] = useState(0);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [openAttemptsModal, setOpenAttemptsModal] = useState(false);
  const [openLeaderboardModal, setOpenLeaderboardModal] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const plan = localStorage.getItem("category");
  const email = localStorage.getItem("userMail");
  const { Endpoint } = useContext(UserContext);

  useEffect(() => {
    fetchProfileData();
    fetchInstitutionData();
    fetchPaperData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const response = await axiosInstance.post(`get/U_ViewProfile.php`, {
        email: email,
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
        }
        setCategory(response.data);
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
    }
  };

  const fetchInstitutionData = async () => {
    try {
      const response = await axiosInstance.post(
        `get/U_ViewNonNursingCategory.php`,
        {
          userId: email,
        }
      );
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
        }
        const filteredData = response.data
          ?.map((item) => ({
            ...item,
            img: `${Endpoint}/controllers/api/admin/upload/${item.img}`,
          }))
          .filter((item) => item.sno == sno);
        setData(filteredData);
      }
    } catch (error) {
      console.error("Error fetching institution data:", error);
    }
  };

  const fetchPaperAttempts = async (pId) => {
    setAttemptsLoading(true);
    try {
      const res = await axiosInstance.post(`get/U_GetStudentTestAttempts.php`, {
        userId: email,
        testName: "non_nursing",
        paperId: pId,
      });
      if (res.data?.status === "success") {
        setAttemptsCount(res.data.total_attempts || 0);
      } else {
        setAttemptsCount(0);
      }
    } catch (error) {
      console.error("Error fetching paper attempts:", error);
      setAttemptsCount(0);
    } finally {
      setAttemptsLoading(false);
    }
  };

  const fetchPaperData = async () => {
    try {
      const response = await axiosInstance.post(
        `get/U_ViewNonNursingPaper.php`,
        {
          id: sno,
          userId: email,
        }
      );
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
        }
        const filteredPapers = response?.data?.filter(
          (paper) => paper.category_id == sno
        );
        setOptions(filteredPapers || []);
      }
    } catch (error) {
      console.error("Error fetching paper data:", error);
    }
  };

  const PaperData = (index, paperCategory) => {
    const selectedPaper = options[index];
    if (!selectedPaper) return;

    setSelectedIndex(index);
    setPaperid(selectedPaper.sno);
    setPaperTitle(selectedPaper.paper_name || "");
    fetchPaperAttempts(selectedPaper.sno);
  };

  useEffect(() => {
    if (options.length > 0 && paperid === null) {
      PaperData(0, options[0].category);
    }
  }, [options, paperid]);

  const startTest = async () => {
    if (!paperid) {
      showWarning("Please select an exam paper from the left panel to begin.");
      return;
    }

    const selectedPaper = options[selectedIndex];
    if (selectedPaper?.category === "premium" && plan !== "premium") {
      showWarning("Please subscribe to continue premium");
      navigate("/plan");
      return;
    }

    if (isStarting) return;

    try {
      setIsStarting(true);
      const res = await axiosInstance.post(`post/U_NonNursingTest.php`, {
        id: email,
        category_id: sno,
        paper_id: paperid,
      });

      if (res.status === 200) {
        if (res.data?.message === "timeout") {
          navigate("/signin");
          return;
        }

        navigate(`/aptitudetest/${sno}/${paperid}`, {
          state: {
            paperName: selectedPaper ? selectedPaper.paper_name : "",
          },
        });
      }
    } catch (error) {
      console.error("Error starting exam:", error);
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <InstructionPageContainer>
      <Container>
        <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: "24px" }}>
          <Link underline="hover" color="inherit" href="" onClick={(e) => { e.preventDefault(); navigate("/home"); }}>
            Home
          </Link>
          <Link underline="hover" color="inherit" href="" onClick={(e) => { e.preventDefault(); navigate("/non_nursing"); }}>
            Non-Nursing MCQ
          </Link>
          <Typography color="text.primary">Instructions</Typography>
        </Breadcrumbs>

        {data.map((val) => (
          <Box key={val.sno}>
            <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px", flexWrap: "wrap", gap: "16px" }}>
              <Box>
                <Typography variant="h1" style={{ fontWeight: 800 }}>
                  {val.category_name}
                </Typography>
                <Typography variant="body1" color="textSecondary" style={{ marginTop: "4px" }}>
                  {val.category_desc}
                </Typography>
              </Box>
              {val.img && (
                <img
                  src={val.img}
                  alt="Category Logo"
                  style={{ maxHeight: "65px", objectFit: "contain", borderRadius: "12px" }}
                />
              )}
            </Box>

            <Row className="g-4">
              <Col xs={12} md={4} lg={3}>
                <SidebarCard>
                  <Box style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", color: "var(--color-primary)" }}>
                    <CalendarMonthIcon />
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                      Select Exam Paper
                    </Typography>
                  </Box>

                  {options.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No paper options found.
                    </Typography>
                  ) : (
                    <List style={{ padding: 0 }}>
                      {options.map((option, index) => (
                        <PaperItemButton
                          key={index}
                          selected={selectedIndex === index}
                          onClick={() => PaperData(index, option.category)}
                        >
                          <ListItemText 
                            primary={
                              <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                                <Typography style={{ fontWeight: selectedIndex === index ? 700 : 500, fontSize: "0.9rem" }}>
                                  {option.paper_name}
                                </Typography>
                                {option.category === "premium" && (
                                  <img src={CrownIcon} height="16px" alt="Crown" />
                                )}
                              </Box>
                            }
                          />
                        </PaperItemButton>
                      ))}
                    </List>
                  )}
                </SidebarCard>
              </Col>

              <Col xs={12} md={8} lg={9}>
                <InstructionDetailsCard>
                  <Box>
                    <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
                      <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <DescriptionIcon color="primary" />
                        <Typography variant="h2" style={{ fontWeight: 800 }}>
                          Candidate Instructions
                        </Typography>
                        {paperTitle && (
                          <Chip label={paperTitle} size="small" color="primary" variant="outlined" style={{ fontWeight: 700 }} />
                        )}
                      </Box>

                      <Box style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <Chip
                          label={attemptsLoading ? "Checking..." : `Attempts: ${attemptsCount}`}
                          color={attemptsCount > 0 ? "primary" : "default"}
                          variant={attemptsCount > 0 ? "filled" : "outlined"}
                          style={{ fontWeight: 700, height: "34px", borderRadius: "10px" }}
                        />
                        {attemptsCount > 0 && (
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<HistoryIcon />}
                            onClick={() => setOpenAttemptsModal(true)}
                            style={{
                              borderRadius: "10px",
                              textTransform: "none",
                              fontWeight: 700,
                              height: "34px",
                              padding: "0 12px",
                              fontSize: "0.8rem",
                            }}
                          >
                            Past Attempts
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          startIcon={<EmojiEventsIcon style={{ color: "#f59e0b" }} />}
                          onClick={() => setOpenLeaderboardModal(true)}
                          style={{
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 700,
                            height: "34px",
                            padding: "0 12px",
                            fontSize: "0.8rem",
                            borderColor: "#f59e0b",
                            color: "#b45309",
                            backgroundColor: "rgba(245, 158, 11, 0.08)",
                          }}
                        >
                          Leaderboard
                        </Button>
                      </Box>
                    </Box>

                    <Box style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                      {val.category_instruction ? (
                        val.category_instruction
                          .split(/\r?\n/)
                          .filter((instruction) => instruction.trim() !== "")
                          .map((instruction, index) => (
                            <Box key={index} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                              <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: "22px", height: "22px", borderRadius: "50%", backgroundColor: "rgba(30, 58, 138, 0.08)", color: "var(--color-primary)", fontWeight: 700, fontSize: "0.75rem", marginTop: "2px" }}>
                                {index + 1}
                              </Box>
                              <Typography variant="body1" color="textPrimary" style={{ lineHeight: 1.6 }}>
                                {instruction.replace(/^\d+\.\s*/, "").trim()}
                              </Typography>
                            </Box>
                          ))
                      ) : (
                        <Typography variant="body1" color="textSecondary">
                          No instructions specified.
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "40px", flexWrap: "wrap", gap: "16px", width: "100%" }}>
                    {/* Left: Test Leaderboard Button */}
                    <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Button
                        variant="outlined"
                        startIcon={<EmojiEventsIcon style={{ color: "#f59e0b" }} />}
                        onClick={() => {
                          if (paperid) {
                            setOpenLeaderboardModal(true);
                          } else {
                            showWarning("Please select an exam paper to view its leaderboard.");
                          }
                        }}
                        style={{
                          borderRadius: "12px",
                          textTransform: "none",
                          fontWeight: 700,
                          height: "44px",
                          padding: "0 18px",
                          borderColor: "#f59e0b",
                          color: "#b45309",
                          backgroundColor: "rgba(245, 158, 11, 0.08)",
                        }}
                      >
                        Leaderboard
                      </Button>
                    </Box>

                    {/* Right: Directly Near Start Exam Button */}
                    <Box style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                      {/* Number of Attempts Badge */}
                      <Chip
                        label={attemptsLoading ? "Checking..." : `No. of Attempts: ${attemptsCount}`}
                        color={attemptsCount > 0 ? "primary" : "default"}
                        variant={attemptsCount > 0 ? "filled" : "outlined"}
                        style={{
                          fontWeight: 700,
                          height: "44px",
                          borderRadius: "12px",
                          fontSize: "0.88rem",
                          padding: "0 4px",
                        }}
                      />

                      {/* Option to View Previous Attempts / Reviews */}
                      {attemptsCount > 0 && (
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<HistoryIcon />}
                          onClick={() => setOpenAttemptsModal(true)}
                          style={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 700,
                            height: "44px",
                            padding: "0 18px",
                            fontSize: "0.88rem",
                            borderColor: "var(--color-primary)",
                            backgroundColor: "rgba(30, 58, 138, 0.05)",
                          }}
                        >
                          View Previous Attempts
                        </Button>
                      )}

                      {/* Start / Re-attempt Exam Button */}
                      <PrimaryButton
                        onClick={startTest}
                        disabled={isStarting || !paperid}
                        endIcon={<ArrowForwardIcon />}
                        style={{
                          height: "46px",
                          borderRadius: "14px",
                          minWidth: "160px",
                          fontSize: "0.95rem",
                          fontWeight: 700,
                        }}
                      >
                        {isStarting
                          ? "Starting Exam..."
                          : attemptsCount > 0
                          ? "Re-attempt Exam"
                          : "Start Exam"}
                      </PrimaryButton>
                    </Box>
                  </Box>
                </InstructionDetailsCard>
              </Col>
            </Row>
          </Box>
        ))}

        {/* Previous Attempts Modal */}
        {openAttemptsModal && (
          <PreviousAttemptsModal
            open={openAttemptsModal}
            onClose={() => setOpenAttemptsModal(false)}
            testName="non_nursing"
            paperId={paperid}
            paperTitle={paperTitle}
          />
        )}

        {/* Test Leaderboard Modal */}
        {openLeaderboardModal && (
          <TestLeaderboardModal
            open={openLeaderboardModal}
            onClose={() => setOpenLeaderboardModal(false)}
            testName="non_nursing"
            paperId={paperid}
            paperTitle={paperTitle}
          />
        )}
      </Container>
    </InstructionPageContainer>
  );
}
